/**
 * Registry of public guide articles at /guides/:slug. Each entry here is
 * metadata only (used by the /guides index, related-guide links, and the
 * sitemap) — the article content lives in its own page component under
 * `pages/guides/`. Keep this in step with the routes added in App.js and
 * with `public/sitemap.xml`.
 */
export const GUIDE_CATEGORIES = {
  START: 'Getting started',
  ORG: 'Requesting from a specific organisation',
  UNDERSTAND: 'Understanding what comes back',
};

export const GUIDES = [
  {
    slug: 'what-is-a-subject-access-request',
    category: GUIDE_CATEGORIES.START,
    title: 'What is a Subject Access Request? A plain-English guide',
    dek: "The law that lets you see every record an organisation holds about you, explained without the legal jargon.",
    description:
      'What a Subject Access Request (SAR) is, who has to respond, how long they have, and how to send one for your own care, school, health or police records.',
    updated: '2026-09-01',
  },
  {
    slug: 'freedom-of-information-vs-subject-access-request',
    category: GUIDE_CATEGORIES.START,
    title: 'Freedom of Information vs a Subject Access Request',
    dek: 'These get mixed up constantly, including by the organisations meant to handle them. Here is why it matters which one you send.',
    description:
      'The difference between a Freedom of Information request and a Subject Access Request, why FOI cannot get you your own care records, and which public bodies FOI actually covers.',
    updated: '2026-09-22',
  },
  {
    slug: 'how-to-request-your-care-records-from-the-council',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your care records from the council',
    dek: 'Social worker notes, case files and review minutes: how to ask your local authority for everything it holds about your time in care.',
    description:
      'Step-by-step guide to requesting your social care records from a UK council, including what to ask for, legal deadlines, and what to do if they refuse or go quiet.',
    updated: '2026-09-01',
    orgType: 'council',
  },
  {
    slug: 'how-to-request-your-fostering-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your fostering records',
    dek: 'What a fostering agency or council holds about your foster placements, and how to ask for it.',
    description:
      'How to request your fostering records in the UK: what a fostering agency and council must give you, matching and placement records, carer reviews, and how long it takes.',
    updated: '2026-09-01',
    orgType: 'fostering_agency',
  },
  {
    slug: 'can-you-send-a-sar-to-a-foster-carer',
    category: GUIDE_CATEGORIES.ORG,
    title: 'Can you send a Subject Access Request to a foster carer?',
    dek: 'Foster carers are not council employees — but that is not actually what decides whether you can get what they wrote about you.',
    description:
      'Whether a care leaver can send a Subject Access Request directly to a former foster carer, what happens to diaries, logs and family newsletters a carer wrote, and who to actually ask.',
    updated: '2026-09-21',
  },
  {
    slug: 'how-to-request-your-childrens-home-records',
    category: GUIDE_CATEGORIES.ORG,
    title: "How to request your children's home records",
    dek: 'Most children\'s homes in England are privately run, not council-run. Who actually holds your file, and what they are legally required to have kept.',
    description:
      "How to request your children's home (residential care) records in the UK, including restraint records, missing-from-care incidents, and what happens if the home has since closed.",
    updated: '2026-09-22',
  },
  {
    slug: 'how-to-request-your-adoption-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your adoption records',
    dek: 'Birth records, life story work and contact arrangements: your right of access as an adopted adult.',
    description:
      'How adopted adults in the UK can request their adoption records, including birth records, life story books and letterbox contact files, and where adoption counselling fits in.',
    updated: '2026-09-01',
    orgType: 'adoption',
  },
  {
    slug: 'how-to-request-your-school-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your school records',
    dek: 'SEN records, behaviour logs and reports: how to ask a school, academy trust or local authority for your education file.',
    description:
      'How to request your school records in the UK, including SEN and EHCP files, behaviour and exclusion records, and who to ask if the school has since closed.',
    updated: '2026-09-01',
    orgType: 'school',
  },
  {
    slug: 'how-to-request-your-medical-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your medical records',
    dek: 'GP notes and hospital files: how to request your health records, and what happens if you were registered under a different name.',
    description:
      'How to request your NHS medical records from a GP surgery or hospital in the UK, what you can ask for, and how to handle gaps from placements or name changes.',
    updated: '2026-09-01',
    orgType: 'doctor',
  },
  {
    slug: 'how-to-request-your-police-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your police records',
    dek: "Missing-from-care reports, intelligence logs and witness statements: your right of access to police records, and where it differs from other requests.",
    description:
      'How to request your police records in the UK under both the UK GDPR and the Data Protection Act 2018, including missing-episode and safeguarding records from your time in care.',
    updated: '2026-09-01',
    orgType: 'police',
  },
  {
    slug: 'how-to-request-your-cafcass-and-family-court-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your Cafcass and family court records',
    dek: 'Two different regimes, easy to mix up: Cafcass will answer an ordinary request, but the court\'s own file is a different, more restrictive process.',
    description:
      'How to request your Cafcass records and, separately, your family court case file from care proceedings — what an ordinary Subject Access Request covers and what needs a court application instead.',
    updated: '2026-09-22',
  },
  {
    slug: 'how-to-request-your-home-office-immigration-records',
    category: GUIDE_CATEGORIES.ORG,
    title: 'How to request your Home Office and immigration records',
    dek: 'For anyone who came into care as an unaccompanied asylum-seeking child — your council file works like anyone else\'s. The Home Office side is where it gets harder.',
    description:
      'How to request your Home Office immigration and asylum file as a care-experienced unaccompanied asylum-seeking child, and the immigration exemption that can apply to it.',
    updated: '2026-09-22',
  },
  {
    slug: 'understanding-redactions-in-your-care-records',
    category: GUIDE_CATEGORIES.UNDERSTAND,
    title: "Understanding redactions in your records — and how to challenge them",
    dek: 'Why parts of your file arrive blacked out, when that is lawful, and how to ask for a redaction to be reviewed.',
    description:
      'Why organisations redact parts of your care, school or medical records, which exemptions are lawful, and how to challenge a redaction you think is wrong or disproportionate.',
    updated: '2026-09-01',
  },
  {
    slug: 'how-long-do-organisations-keep-your-records',
    category: GUIDE_CATEGORIES.UNDERSTAND,
    title: 'How long do organisations keep your records?',
    dek: 'A common worry is that you have already left it too late. For almost everyone, records are kept for decades — organisation by organisation, here is roughly how long.',
    description:
      'How long councils, fostering agencies, adoption agencies, schools, the NHS, Cafcass and the police keep your childhood records in the UK, and what to do if you are told yours were destroyed early.',
    updated: '2026-09-22',
  },
  {
    slug: 'what-to-do-if-an-organisation-misses-the-deadline',
    category: GUIDE_CATEGORIES.UNDERSTAND,
    title: 'What to do if an organisation misses the deadline',
    dek: 'Your Subject Access Request is overdue. Here is exactly what to do next, including how and when to involve the ICO.',
    description:
      'What to do if a council, school or other organisation misses the one-month Subject Access Request deadline, including how to chase them and how to complain to the ICO.',
    updated: '2026-09-01',
  },
  {
    slug: 'how-to-find-out-why-you-were-taken-into-care',
    category: GUIDE_CATEGORIES.UNDERSTAND,
    title: 'How to find out why you were taken into care',
    dek: 'The answer is almost always written down, often in more than one place — where to look first, and where to look if that is not enough.',
    description:
      'How to find out why you were taken into care: the Letter Before Proceedings and Threshold Statement in your council file, when police records hold the first-hand account, and what Cafcass and the court can add.',
    updated: '2026-09-23',
  },
  {
    slug: 'what-to-do-if-your-records-are-wrong-or-unkind',
    category: GUIDE_CATEGORIES.UNDERSTAND,
    title: 'What to do if something in your records is wrong, or was written unkindly',
    dek: 'A note that says something happened that you know did not, or an account of you that never gave you a chance to answer back. Here is what you can actually do.',
    description:
      'What to do if you read something untrue or unkind in your care records — your right to have it corrected, how councils must handle a dispute, and where to get support while you read.',
    updated: '2026-09-22',
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug) || null;

export const relatedGuides = (slug, max = 3) => {
  const current = getGuide(slug);
  if (!current) return GUIDES.slice(0, max);
  const sameCategory = GUIDES.filter((g) => g.slug !== slug && g.category === current.category);
  const rest = GUIDES.filter((g) => g.slug !== slug && g.category !== current.category);
  return [...sameCategory, ...rest].slice(0, max);
};
